import { Injectable } from '@angular/core';
import { AppConfig } from '../../config/config.api';
import { StorageService } from './storage.service';
import 'rxjs-compat/add/operator/map';
import { select, Store } from '@ngrx/store';
import { getUser, getToken } from 'src/app/store/selectors/auth.selectors';
import { HttpClient, HttpParams } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { BusinessTypeEnum } from '../enums/BusinessTypeEnum';
import { environment } from '../../environments/environment';

@Injectable()
export class RequestsServices extends AppConfig {
  private get headers() {
    return this.getHeaders();
  }

  user;
  accessToken;

  constructor(
    public http: HttpClient,
    public storage: StorageService,
    private store: Store,
    private cookieService: CookieService,
  ) {
    super();

    this.store.pipe(select(getToken)).subscribe(accessToken => this.accessToken = accessToken);
    this.store.pipe(select(getUser)).subscribe(user => this.user = user);
  }

  public get(requestParams: IRequestParam ): Promise<any> {
    const apiEndpoint = this.getEndpoint(requestParams.endPoint);
    if (requestParams.params) {
      const queryParams = this.getParams(requestParams.params);
      return this.http.get(`${this.BaseUrl}${this.Path[requestParams.path]}${apiEndpoint}`,
        {headers: this.headers, params: queryParams}).toPromise();
    }

    const pathParam = requestParams.pathParam ? requestParams.pathParam : '';
    if (requestParams.partnership) {
      return this.http
        .get(`${this.BaseUrl}${this.Path[requestParams.path]}${pathParam}${apiEndpoint}`, {headers: this.headers}).toPromise();
    }

    if (requestParams.custom) {
      return this.http
        .get(`${this.BaseUrl}${this.Path[requestParams.path]}${requestParams.custom}${apiEndpoint}${pathParam}`, {headers: this.headers})
        .toPromise();
    }

    return this.http.get(`${this.BaseUrl}${this.Path[requestParams.path]}${apiEndpoint}${pathParam}`, {headers: this.headers})
        .toPromise();
  }

  private getEndpoint(apiEndpoint): string {
    if (apiEndpoint) {
      const mappedApiEndpoint = this.ApiEndPoints[apiEndpoint];
      if (mappedApiEndpoint) {
        return mappedApiEndpoint;
      }
      return apiEndpoint;
    }
    return '';
  }

  public post(path: string, data: any, endPoint?: string, custom?: string): Promise<any> {
    endPoint = this.getEndpoint(endPoint);
    if (endPoint) {
      return this.http.post(`${this.BaseUrl}${this.Path[path]}${endPoint}`,
        data, {headers: this.headers}).toPromise();
    } else if (custom) {
      return this.http.post(`${this.BaseUrl}${this.Path[path]}${custom}`,
        data, {headers: this.headers}).toPromise();
    } else {
      return this.http.post(`${this.BaseUrl}${this.Path[path]}`,
        data, {headers: this.headers}).toPromise();
    }
  }

  public put(path: string, data: any, endPoint?: any, custom?: string): Promise<any> {
    endPoint = this.getEndpoint(endPoint);
    if (endPoint) {
      return this.http.put(`${this.BaseUrl}${this.Path[path]}${endPoint}`,
        data, {headers: this.headers}).toPromise();
    } else if (custom) {
      return this.http.put(`${this.BaseUrl}${this.Path[path]}${custom}`,
        data, {headers: this.headers}).toPromise();
    } else {
      return this.http.put(`${this.BaseUrl}${this.Path[path]}`,
        data, {headers: this.headers}).toPromise();
    }
  }

  public patch(path: string, data: any, endPoint?: any, custom?: string): Promise<any> {
    endPoint = this.getEndpoint(endPoint);
    if (endPoint) {
      return this.http.patch(`${this.BaseUrl}${this.Path[path]}${endPoint}`,
        data, {headers: this.headers}).toPromise();
    } else if (custom) {
      return this.http.patch(`${this.BaseUrl}${this.Path[path]}${custom}`,
        data, {headers: this.headers}).toPromise();
    } else {
      return this.http.patch(`${this.BaseUrl}${this.Path[path]}`,
        data, {headers: this.headers}).toPromise();
    }
  }

  public delete(path: string, endPoint?: any, custom?: string): Promise<any> {
    endPoint = this.getEndpoint(endPoint);
    if (endPoint) {
      return this.http.delete(`${this.BaseUrl}${this.Path[path]}${endPoint}`,
        {headers: this.headers}).toPromise();
    } else if (custom) {
      return this.http.delete(`${this.BaseUrl}${this.Path[path]}${custom}`,
        {headers: this.headers}).toPromise();
    } else {
      return this.http.delete(`${this.BaseUrl}${this.Path[path]}`,
        {headers: this.headers}).toPromise();
    }
  }

  private getParams(queryParams: any): HttpParams {
    if (!Array.isArray(queryParams)) {
      return this.parseObjectParams(queryParams);
    }

    let params: HttpParams = new HttpParams();
    queryParams.forEach((element) => {
      params = params.append(element.param, element.value);
    });

    return params;
  }

  private parseObjectParams(params: any) {
    let httpParams = new HttpParams();

    Object.keys(params).forEach(key => {
      httpParams = httpParams.append(key, params[key]);
    });

    return httpParams;
  }

  public download(path: string) {
    window.open(`${this.BaseUrl}${path}`);
  }

  private getHeaders() {
    const headers: { [name: string]: string | string[]; } = {};
    const token = this.cookieService.get(environment.COOKIE_AT);
    const businessType = this.cookieService.get(environment.COOKIE_BT);
    const businessId = this.cookieService.get(environment.COOKIE_BID);

    if (!token) {
      return headers;
    }

    headers.authorization = token;

    if (businessId) {
      this.setBusinessHeader(businessType, headers, businessId);
    }

    if (this.user) {
      headers.customerId = this.user.customerId;
    }

    return headers;
  }

  private setBusinessHeader(businessType: string, headers: any, businessId: string) {
    switch (businessType) {
      case BusinessTypeEnum.Company:
        headers.companyId = businessId;
        break;
      case BusinessTypeEnum.Establishment:
        headers.establishmentId = businessId;
        break;
      default:
        break;
    }
  }
}

interface IRequestParam {
  path: string;
  partnership?: boolean;
  endPoint?: string;
  params?: any;
  pathParam?: string;
  custom?: string;
}

